import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import CmsPageLayout from "../components/CmsPageLayout";
import CmsPageRoot from "../components/CmsPageRoot";
import CmsPageShell from "../components/CmsPageShell";
import ValidationSummary from "../components/ValidationSummary";
import StickyFormActions from "../components/StickyFormActions";
import PreviewJsonModal from "../components/PreviewJsonModal";
import { CMS_LANGUAGES, CMS_PAGE_META, DEFAULT_ABOUT } from "../constants";
import { validateAboutPageBilingual } from "../utils/validation";
import { applyCmsValidationFailure, scrollToFirstValidationError } from "../utils/validationUi";
import { fetchPageContent, saveAboutContent, uploadMedia } from "../api/cmsApi";
import { useCmsAuth } from "../hooks/useCmsAuth";
import {
  MarketingHeroSection,
  ProgramCtaSection,
  HexFeaturesSection,
} from "../sections/SharedCmsSections";

const PAGE_META = CMS_PAGE_META.about;
const EN_ID = CMS_LANGUAGES.en.id;
const ES_ID = CMS_LANGUAGES.es.id;
const cloneAbout = () => JSON.parse(JSON.stringify(DEFAULT_ABOUT));

const ABOUT_SECTION_MAP = [
  { id: "hero", prefixes: ["hero."] },
  { id: "features", prefixes: ["features."] },
  { id: "programCta", prefixes: ["programCta."] },
];

function AboutPageEditor() {
  const { token } = useCmsAuth();
  const [aboutEn, setAboutEn] = useState(() => cloneAbout());
  const [aboutEs, setAboutEs] = useState(() => cloneAbout());
  const [initialEn, setInitialEn] = useState(() => cloneAbout());
  const [initialEs, setInitialEs] = useState(() => cloneAbout());
  const [updatedAtEn, setUpdatedAtEn] = useState(null);
  const [updatedAtEs, setUpdatedAtEs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadingKey, setUploadingKey] = useState(null);

  const dirtyEn = JSON.stringify(aboutEn) !== JSON.stringify(initialEn);
  const dirtyEs = JSON.stringify(aboutEs) !== JSON.stringify(initialEs);
  const dirty = dirtyEn || dirtyEs;

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErrors({});
    try {
      const [enResult, esResult] = await Promise.all([
        fetchPageContent(token, EN_ID),
        fetchPageContent(token, ES_ID),
      ]);
      const enMerged = { ...cloneAbout(), ...(enResult.content.about || {}) };
      const esMerged = { ...cloneAbout(), ...(esResult.content.about || {}) };
      setAboutEn(enMerged);
      setAboutEs(esMerged);
      setInitialEn(JSON.parse(JSON.stringify(enMerged)));
      setInitialEs(JSON.parse(JSON.stringify(esMerged)));
      setUpdatedAtEn(enResult.updatedAt);
      setUpdatedAtEs(esResult.updatedAt);
    } catch (err) {
      const fallback = cloneAbout();
      setAboutEn(fallback);
      setAboutEs(cloneAbout());
      setInitialEn(JSON.parse(JSON.stringify(fallback)));
      setInitialEs(JSON.parse(JSON.stringify(cloneAbout())));
      toast.error("Could not load from server. Restore CMS data in MongoDB.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSave = async () => {
    const validationErrors = validateAboutPageBilingual(aboutEn, aboutEs);
    if (Object.keys(validationErrors).length) {
      applyCmsValidationFailure({
        validationErrors,
        setErrors,
        sectionPrefixMap: ABOUT_SECTION_MAP,
        toastFn: (message) => toast.error(message),
      });
      return;
    }
    setSaving(true);
    try {
      const [enResult, esResult] = await Promise.all([
        saveAboutContent(token, EN_ID, aboutEn),
        saveAboutContent(token, ES_ID, aboutEs),
      ]);
      setInitialEn(JSON.parse(JSON.stringify(aboutEn)));
      setInitialEs(JSON.parse(JSON.stringify(aboutEs)));
      setErrors({});
      setUpdatedAtEn(enResult.updatedAt || new Date().toISOString());
      setUpdatedAtEs(esResult.updatedAt || new Date().toISOString());
      toast.success("Saved — live on the website (English & Spanish).");
    } catch (err) {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (lang, key, file, onSuccess) => {
    setUploadingKey(`${lang}.${key}`);
    try {
      const publicUrl = await uploadMedia(token, file);
      if (publicUrl) {
        onSuccess(publicUrl);
        toast.success("Image uploaded.");
      } else {
        toast.error("No URL returned from upload.");
      }
    } catch (err) {
      toast.error("Upload failed.");
    } finally {
      setUploadingKey(null);
    }
  };

  const sectionProps = {
    errors,
    onImageUpload: handleImageUpload,
    uploadingKey,
  };

  return (
    <CmsPageRoot>
      <CmsPageLayout
        actions={
          <StickyFormActions
            dirty={dirty}
            saving={saving}
            onSave={handleSave}
            onPreview={() => setPreviewOpen(true)}
          />
        }
      >
      <CmsPageShell
        pageIcon={PAGE_META.pageIcon}
        pageIconSrc={PAGE_META.tabIcon}
        title={PAGE_META.title}
        subtitle={PAGE_META.subtitle}
        dirtyEn={dirtyEn}
        dirtyEs={dirtyEs}
        updatedAtEn={updatedAtEn}
        updatedAtEs={updatedAtEs}
        loading={loading}
        skeletonPage="about"
      >
        <ValidationSummary
          errors={errors}
          onJumpToFirst={() =>
            scrollToFirstValidationError(errors, { sectionPrefixMap: ABOUT_SECTION_MAP })
          }
        />
        <MarketingHeroSection
          id="hero"
          title="Hero"
          description="Top banner"
          heroEn={aboutEn.hero || {}}
          heroEs={aboutEs.hero || {}}
          setHeroEn={(hero) => setAboutEn({ ...aboutEn, hero })}
          setHeroEs={(hero) => setAboutEs({ ...aboutEs, hero })}
          {...sectionProps}
        />
        <HexFeaturesSection
          featuresEn={aboutEn.features || []}
          featuresEs={aboutEs.features || []}
          setFeaturesEn={(features) => setAboutEn({ ...aboutEn, features })}
          setFeaturesEs={(features) => setAboutEs({ ...aboutEs, features })}
          {...sectionProps}
        />
        <ProgramCtaSection
          ctaEn={aboutEn.programCta || {}}
          ctaEs={aboutEs.programCta || {}}
          setCtaEn={(programCta) => setAboutEn({ ...aboutEn, programCta })}
          setCtaEs={(programCta) => setAboutEs({ ...aboutEs, programCta })}
          {...sectionProps}
        />
      </CmsPageShell>
      </CmsPageLayout>
      <PreviewJsonModal
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title="About page payload"
        payload={{ en: { about: aboutEn }, es: { about: aboutEs } }}
      />
    </CmsPageRoot>
  );
}

export default AboutPageEditor;
